import { Maximize2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button, Markdown } from '@/components/ui'
import { stripDuplicateTitleHeading } from '@/modules/analysis/components/AnalysisReportSection/headings'
import { ReportChart } from '@/modules/analysis/components/AnalysisReportSection/ReportChart'
import { ReportTable } from '@/modules/analysis/components/AnalysisReportSection/ReportTable'
import { REPORT_PROSE } from '@/modules/analysis/components/AnalysisReportSection/sectionProse'
import {
  placedArtifactIds,
  splitMarkdownTokens,
} from '@/modules/analysis/components/AnalysisReportSection/tokens'
import type { ReportSection } from '@/modules/analysis/stores/types'
import {
  useCancelledReportEditTokens,
  useChatMessages,
  useChatMutations,
} from '@/modules/chat/hooks'
import { type StandardError, useAsyncError } from '@/modules/errors'
import { cn } from '@/utils'
import {
  ConfirmationFooter,
  useConfirmationCardState,
} from './confirmationCard'
import ExpandedArtifactDialog from './ExpandedArtifactDialog'
import {
  pendingSiblingReportEditTokens,
  REPORT_EDIT_APPLY_TOOL,
} from './pendingReportEditTokens'

export type ReportEditOperation = 'rewrite' | 'add' | 'remove' | 'move'

// Shallow camelCase leaves the anchor key as lukita sends it.
export type ReportEditPosition = 'start' | 'end' | { after_section_id: string }

export interface ReportEditPreviewSpec {
  token: string
  operation: ReportEditOperation
  sectionId: string
  oldSection: ReportSection | null
  newSection: ReportSection | null
  position?: ReportEditPosition | null
  expiresAt: string
}

interface ReportEditPreviewArtifactProps {
  slug: string
  spec: ReportEditPreviewSpec
}

const OPERATION_LABELS: Record<ReportEditOperation, string> = {
  rewrite: 'Rewrite section',
  add: 'Add section',
  remove: 'Remove section',
  move: 'Move section',
}

export default function ReportEditPreviewArtifact({
  slug,
  spec,
}: ReportEditPreviewArtifactProps) {
  const { sendMessage, cancelReportEdit } = useChatMutations()
  const throwAsyncError = useAsyncError()
  const messages = useChatMessages()
  const cancelledTokens = useCancelledReportEditTokens()
  const [appliedOnCancel, setAppliedOnCancel] = useState(false)
  const { state, disabled, setRequested } = useConfirmationCardState({
    ...REPORT_EDIT_APPLY_TOOL,
    token: spec.token,
    expiresAt: spec.expiresAt,
    cancelled: cancelledTokens.includes(spec.token),
    applied: appliedOnCancel,
  })
  const [expanded, setExpanded] = useState(false)
  const groupTokens = useMemo(
    () => pendingSiblingReportEditTokens(messages, spec.token, cancelledTokens),
    [messages, spec.token, cancelledTokens],
  )
  const headline = headlineFor(spec)

  function sendConfirmation(message: string, tokens: string[]) {
    setRequested(true)
    const confirmation =
      tokens.length > 1
        ? { kind: 'report_edit' as const, tokens }
        : { kind: 'report_edit' as const, token: spec.token }
    sendMessage(slug, message, confirmation).catch((error: Error) => {
      setRequested(false)
      throwAsyncError(error)
    })
  }

  function applyEdit() {
    sendConfirmation('Apply the report edit.', [spec.token])
  }

  function applyAll() {
    sendConfirmation(
      `Apply all ${groupTokens.length} report edits.`,
      groupTokens,
    )
  }

  function cancelEdit() {
    cancelReportEdit(slug, spec.token).catch((error: StandardError) => {
      // A 409 means the edit was already applied, so there is nothing to cancel.
      if (error.status === 409) setAppliedOnCancel(true)
      else throwAsyncError(error)
    })
  }

  const applyAllButton =
    state.status === 'pending' && groupTokens.length > 1 ? (
      <Button
        type="button"
        theme="ghost"
        size="xs"
        disabled={disabled}
        onClick={() => {
          setExpanded(false)
          applyAll()
        }}
      >
        Apply all {groupTokens.length}
      </Button>
    ) : null

  const footerProps = {
    expiresAt: spec.expiresAt,
    state,
    disabled,
    appliedMessage: 'Applied. The report has been updated.',
    expiredMessage: 'Expired. Ask the assistant to propose the edit again.',
    confirmLabel: 'Apply edit',
    extraAction: applyAllButton,
  }

  return (
    <figure className="my-1 w-full overflow-hidden rounded-xl border bg-card shadow-sm">
      <figcaption className="flex items-center justify-between gap-3 border-b px-4 py-2.5 text-sm font-medium text-foreground">
        <span className="truncate">{headline}</span>
        <Button
          type="button"
          theme="ghost"
          size="xs"
          isIcon
          className="shrink-0"
          onClick={() => setExpanded(true)}
          title="Expand"
          aria-label="Expand"
        >
          <Maximize2 className="size-3.5" aria-hidden />
        </Button>
      </figcaption>

      <SectionPreview slug={slug} spec={spec} />

      <ConfirmationFooter
        {...footerProps}
        onApply={applyEdit}
        onCancel={cancelEdit}
      />

      <ExpandedArtifactDialog
        open={expanded}
        onOpenChange={setExpanded}
        title={headline}
      >
        <SectionPreview
          slug={slug}
          spec={spec}
          expanded
          className="max-h-none flex-1"
        />
        <ConfirmationFooter
          {...footerProps}
          onApply={() => {
            setExpanded(false)
            applyEdit()
          }}
          onCancel={() => {
            setExpanded(false)
            cancelEdit()
          }}
        />
      </ExpandedArtifactDialog>
    </figure>
  )
}

function headlineFor(spec: ReportEditPreviewSpec): string {
  const section = spec.newSection ?? spec.oldSection
  const title = section?.title ?? spec.sectionId
  if (spec.operation === 'move') {
    return `Move: ${title} ${destinationFor(spec.position)}`
  }
  return `${OPERATION_LABELS[spec.operation]}: ${title}`
}

function destinationFor(position: ReportEditPosition | null | undefined) {
  if (position === 'start') return 'to the start'
  if (position && typeof position === 'object') {
    return `after ${position.after_section_id}`
  }
  return 'to the end'
}

interface SectionPreviewProps {
  slug: string
  spec: ReportEditPreviewSpec
  expanded?: boolean
  className?: string
}

function SectionPreview({
  slug,
  spec,
  expanded = false,
  className,
}: SectionPreviewProps) {
  // A removal has no proposed section, so the section being deleted is the
  // preview; rewrite/add show the proposed section, with the current one
  // collapsed behind a disclosure for comparison.
  const primarySection = spec.newSection ?? spec.oldSection
  const showCurrent = spec.operation === 'rewrite' && spec.oldSection

  return (
    <div
      className={cn(
        'overflow-auto scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400 scrollbar-track-transparent',
        expanded ? '' : 'max-h-80',
        className,
      )}
    >
      {primarySection && (
        <SectionBody
          slug={slug}
          section={primarySection}
          muted={spec.operation === 'remove'}
        />
      )}
      {showCurrent && spec.oldSection && (
        <details className="border-t bg-foreground/[0.02] px-4 py-2">
          <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
            Show current section
          </summary>
          <div className="py-2 opacity-70">
            <SectionBody slug={slug} section={spec.oldSection} />
          </div>
        </details>
      )}
    </div>
  )
}

interface SectionBodyProps {
  slug: string
  section: ReportSection
  muted?: boolean
}

// Mirrors ReportSectionBlock's body composition (markdown + inline
// tables/charts placed by {{table:id}}/{{chart:id}} tokens) without its
// section chrome (heading, numbering) — the headline already lives in the
// card's figcaption.
function SectionBody({ slug, section, muted }: SectionBodyProps) {
  const tables = section.tables ?? []
  const charts = section.charts ?? []
  const segments = splitMarkdownTokens(
    stripDuplicateTitleHeading(section.markdown ?? '', section.title),
  )
  const placed = placedArtifactIds(segments)
  const tableById = new Map(tables.map((table) => [table.id ?? '', table]))
  const chartById = new Map(charts.map((chart) => [chart.id ?? '', chart]))

  return (
    <div
      className={cn('space-y-5 px-4 py-3', muted && 'opacity-60 line-through')}
    >
      {segments.map((segment, index) => {
        if (segment.kind === 'text') {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: segments are static per render
            <Markdown
              key={`text-${index}`}
              content={segment.text}
              componentsOverride={REPORT_PROSE}
            />
          )
        }
        if (segment.kind === 'table') {
          const table = tableById.get(segment.id)
          return table ? (
            <ReportTable key={`table-${segment.id}`} slug={slug} spec={table} />
          ) : null
        }
        const chart = chartById.get(segment.id)
        return chart ? (
          <ReportChart key={`chart-${segment.id}`} spec={chart} />
        ) : null
      })}
      {tables
        .filter((table) => !placed.has(table.id ?? ''))
        .map((table, index) => (
          <ReportTable
            key={table.id ?? `unplaced-table-${index}`}
            slug={slug}
            spec={table}
          />
        ))}
      {charts
        .filter((chart) => !placed.has(chart.id ?? ''))
        .map((chart, index) => (
          <ReportChart
            key={chart.id ?? `unplaced-chart-${index}`}
            spec={chart}
          />
        ))}
    </div>
  )
}
